
import { z } from 'zod';
import { SignupFormField } from '@/schemas/signupFormSchema';

// Ensure a field has all required properties
export function ensureFieldProperties(field: Partial<SignupFormField>): SignupFormField {
  return {
    id: field.id || `field-${Date.now()}`,
    name: field.name || '',
    label: field.label || '',
    type: field.type || 'text',
    visible: field.visible !== undefined ? field.visible : true,
    required: field.required !== undefined ? field.required : false,
    defaultValue: field.defaultValue || '',
    email: field.email || '',
  };
}

// Create a Zod schema from a dynamic form config
export function createDynamicSchema(fields: SignupFormField[]) {
  const schemaObj: Record<string, any> = {};

  fields.forEach((field) => {
    if (!field.visible) return;

    let fieldSchema: any;
    
    switch (field.type) {
      case 'email':
        fieldSchema = z.string().email('Please enter a valid email address');
        break;
      case 'number':
        fieldSchema = z.string().refine((val) => !isNaN(Number(val)), 'Please enter a valid number');
        break;
      case 'tel':
        fieldSchema = z.string().min(5, 'Please enter a valid phone number');
        break;
      case 'password':
        fieldSchema = z.string().min(8, 'Password must be at least 8 characters');
        break;
      case 'checkbox':
        fieldSchema = z.boolean().refine((val) => val === true, 'You must accept the terms');
        break;
      case 'date':
        fieldSchema = z.string().refine((val) => {
          try {
            return !isNaN(new Date(val).getTime());
          } catch {
            return false;
          }
        }, 'Please enter a valid date');
        break;
      default:
        fieldSchema = z.string();
        break;
    }

    // Add required validation
    if (field.required) {
      if (field.type !== 'checkbox') {
        fieldSchema = z.string().min(1, `${field.label} is required`);
      }
    } else {
      // For optional fields
      if (field.type !== 'checkbox') {
        fieldSchema = z.string().optional();
      } else {
        fieldSchema = z.boolean().optional();
      }
    }

    // Special case: confirm password
    if (field.name === 'confirmPassword') {
      // We'll handle this comparison later with refine()
      schemaObj[field.name] = fieldSchema;
    } else {
      schemaObj[field.name] = fieldSchema;
    }
  });
  
  // Create basic schema
  let schema = z.object(schemaObj);

  // Add special validations
  const hasPassword = fields.some(f => f.name === 'password');
  const hasConfirmPassword = fields.some(f => f.name === 'confirmPassword');

  // Add password match validation if both fields exist
  if (hasPassword && hasConfirmPassword) {
    schema = schema.refine(
      (data) => data.password === data.confirmPassword,
      {
        message: "Passwords don't match",
        path: ["confirmPassword"],
      }
    ) as any; // Using 'as any' to avoid type conflicts
  }
  
  return schema;
}

// Create default values from fields
export function createDefaultValues(fields: SignupFormField[]) {
  const defaultValues: Record<string, any> = {};
  
  fields.forEach(field => {
    if (field.visible) {
      if (field.type === 'checkbox') {
        defaultValues[field.name] = Boolean(field.defaultValue === 'true');
      } else {
        defaultValues[field.name] = field.defaultValue || '';
      }
    }
  });
  
  return defaultValues;
}
